//
// src/utils/printer.js
//
// Central receipt-printing client for FastAPIPOS.
//
// Responsibilities:
// - Validate sale data before printing
// - Send structured sale information to the backend
// - Allow src/api.js to attach authentication and tenant headers
// - Avoid duplicating ESC/POS formatting in the browser
//
// The backend is responsible for:
// - Validating the authenticated tenant
// - Applying tenant branding
// - Resolving the tenant printer device
// - Calculating/validating VAT values
// - Producing ESC/POS output
//

import API from "../api";


// =====================================================
// Helpers
// =====================================================

function toNumber(
  value,
  fallback = 0
) {
  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : fallback;
}


function normalizeItem(
  item
) {
  return {
    product_id:
      item?.product_id ??
      item?.id ??
      null,

    productname:
      String(
        item?.productname ||
        item?.name ||
        "Item"
      ).trim(),

    quantity:
      toNumber(
        item?.quantity,
        1
      ),

    price:
      toNumber(
        item?.price,
        0
      ),

    is_taxable:
      item?.is_taxable !==
      false,

    vat_rate:
      toNumber(
        item?.vat_rate,
        item?.is_taxable === false
          ? 0
          : 15
      ),
  };
}


function normalizeSale(
  sale
) {
  if (
    !sale ||
    typeof sale !== "object"
  ) {
    throw new Error(
      "No valid sale data was provided for printing."
    );
  }


  const items =
    Array.isArray(
      sale.items
    )
      ? sale.items.map(
          normalizeItem
        )
      : [];


  if (items.length === 0) {
    throw new Error(
      "The receipt contains no sale items."
    );
  }


  const saleId =
    sale.id ??
    sale.sale_id ??
    null;


  if (!saleId) {
    throw new Error(
      "The sale does not contain a valid receipt number."
    );
  }


  return {
    id:
      saleId,

    receipt_number:
      sale.receipt_number ??
      saleId,

    username:
      sale.username ??
      sale.cashier_name ??
      sale.cashier ??
      null,

    timestamp:
      sale.timestamp ??
      sale.created_at ??
      new Date().toISOString(),

    items,

    total:
      toNumber(
        sale.total,
        0
      ),

    tendered:
      toNumber(
        sale.tendered,
        sale.total
      ),

    change:
      toNumber(
        sale.change,
        0
      ),

    payment_method:
      sale.payment_method ??
      "Cash",

    total_exclusive:
      sale.total_exclusive !==
      undefined
        ? toNumber(
            sale.total_exclusive,
            0
          )
        : undefined,

    total_vat:
      sale.total_vat !==
      undefined
        ? toNumber(
            sale.total_vat,
            0
          )
        : undefined,
  };
}


// =====================================================
// Error Helpers
// =====================================================

function getPrintErrorMessage(
  error
) {
  const detail =
    error?.response
      ?.data
      ?.detail;


  if (
    typeof detail ===
    "string" &&
    detail.trim()
  ) {
    return detail;
  }


  if (
    detail &&
    typeof detail ===
    "object"
  ) {
    if (
      typeof detail.message ===
      "string"
    ) {
      return detail.message;
    }
  }


  if (
    typeof error?.message ===
    "string" &&
    error.message.trim()
  ) {
    return error.message;
  }


  return (
    "The receipt could not be sent to the printer."
  );
}


// =====================================================
// Print Receipt
// =====================================================

export async function printReceipt({
  sale,
}) {
  if (
    !sale ||
    typeof sale !== "object"
  ) {
    throw new Error(
      "No valid sale data was provided for printing."
    );
  }

  const saleId =
    sale.id ??
    sale.sale_id ??
    null;

  if (!saleId) {
    throw new Error(
      "The sale does not contain a valid receipt number."
    );
  }

  try {
    const response =
      await API.post(
        "/sales/print",
        {
          sale_id:
            saleId,
        }
      );

    return (
      response?.data ?? {
        status: "SUCCESS",
      }
    );

  } catch (error) {
    const detail =
      error?.response?.data?.detail;

    const message =
      typeof detail === "string"
        ? detail
        : (
            detail?.message ||
            error?.message ||
            "The receipt could not be printed."
          );

    console.error(
      "Receipt printing failed:",
      {
        sale_id:
          saleId,

        status:
          error?.response?.status,

        message,
      }
    );

    throw new Error(
      message
    );
  }
}

// =====================================================
// Compatibility Alias
// =====================================================
//
// Existing pages may import:
//
//     printReceipt({ sale })
//
// Keep this export shape stable.
//

export default printReceipt;